import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Lightning, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      toast.success(data.message || "Reset link sent if the account exists");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-emerald-600 flex items-center justify-center">
            <Lightning weight="fill" className="text-zinc-950" size={20} />
          </div>
          <span className="font-heading font-black text-lg text-zinc-50">DVEPL</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-2">Account Recovery</div>
        <h2 className="font-heading text-3xl font-black tracking-tight text-zinc-50 mb-3">Forgot password</h2>

        {sent ? (
          <div className="space-y-4" data-testid="forgot-sent-state">
            <div className="flex items-start gap-3 border border-zinc-800 bg-zinc-900/50 rounded-sm p-4">
              <EnvelopeSimple size={22} className="text-emerald-400 mt-0.5 shrink-0" />
              <p className="text-sm text-zinc-300">
                If an account exists for <span className="text-zinc-100 font-medium">{email}</span>, we've sent a password reset link. Check your inbox (and spam) — the link expires in 1 hour.
              </p>
            </div>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="back-to-login">
              <ArrowLeft size={15} /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-zinc-400 mb-6">Enter your account email and we'll send you a link to reset your password.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-email"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2.5 text-sm text-zinc-100 focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600 transition-colors"
                  placeholder="you@company.com"
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold h-11">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-emerald-300 transition-colors" data-testid="back-to-login">
              <ArrowLeft size={15} /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
